import hydraulicDataConfig from '@/api/config/hydraulicDataConfig'
import { queryKeys } from '@/constant/common'
import { APIResponseList } from '@/types/common'
import { HydraulicType } from '@/types/hydraulic'
import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
} from '@tanstack/react-query'

type HydraulicInfiniteQueryKey = [string, number | null, string | undefined]

interface HydraulicInfinityListParams {
  interfaceId: number | null
  keyword?: string
}

/**
 * Infinite query hook for fetching hydraulic pipes with pagination support.
 * Backs the DataGrid's block row model in HydraulicTable.
 */
export default function useSearchHydraulicInfinityList(
  params: HydraulicInfinityListParams,
  queryOptions?: UseInfiniteQueryOptions<
    APIResponseList<HydraulicType>,
    unknown,
    APIResponseList<HydraulicType>,
    APIResponseList<HydraulicType>,
    HydraulicInfiniteQueryKey
  >,
): UseInfiniteQueryResult<APIResponseList<HydraulicType>, unknown> {
  const { interfaceId, keyword } = params

  const isEnabled =
    (queryOptions?.enabled ?? true) &&
    interfaceId !== null &&
    interfaceId !== undefined

  return useInfiniteQuery<
    APIResponseList<HydraulicType>,
    unknown,
    APIResponseList<HydraulicType>,
    HydraulicInfiniteQueryKey
  >(
    [queryKeys.hydraulic, interfaceId, keyword],

    async ({ pageParam = 1 }) => {
      const { data } = await hydraulicDataConfig.getAll({
        interfaceId: interfaceId as number,
        PageSize: 20,
        PageIndex: pageParam as number,
        Keyword: keyword,
      })

      if (!data) {
        return {
          list: [],
          page: 1,
          size: 0,
          totalCount: 0,
          totalPage: 1,
          unpinnedStations: 0,
        }
      }

      return {
        list: data?.data ?? [],
        page: data.page,
        size: data.size,
        sort: data.sort,
        totalCount: data.totalCount,
        totalPage: data.totalPage,
        unpinnedStations: 0,
      }
    },

    {
      ...queryOptions,
      enabled: isEnabled,
      getNextPageParam: (lastPage) => {
        if (!lastPage) return undefined
        return lastPage.page < lastPage.totalPage
          ? lastPage.page + 1
          : undefined
      },
    },
  )
}
